import {gql} from '@apollo/client';
import stringify from 'json-stable-stringify';

import {assert} from 'Asserts';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import {PlateSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  GetContainerConcentration,
  GetContainerConcentrationVariables,
} from 'notebook/integrations/samples/transfer-table-columns/__generated/GetContainerConcentration';
import {
  GetPlateWithWellConcentrations,
  GetPlateWithWellConcentrationsVariables,
} from 'notebook/integrations/samples/transfer-table-columns/__generated/GetPlateWithWellConcentrations';
import {extractContainerId} from 'notebook/integrations/samples/transfer-table-columns/extractContainerId';
import extractPlateId from 'notebook/integrations/samples/transfer-table-columns/extractPlateId';
import {getNonnegativeCustomEvalResult} from 'notebook/integrations/samples/transfer-table-columns/InventoryTableQuantityCustomEval';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {ConcentrationColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomEval} from 'notebook/tables/constants/CustomEvalConstants';
import {
  CustomResolutionQueryType,
  InventoryTableConcentrationAmountFromContainerQuery,
  InventoryTableConcentrationAmountFromPlateQuery,
  InventoryTableConcentrationUnitsFromContainerQuery,
  InventoryTableConcentrationUnitsFromPlateQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {
  formatTypedValue,
  isTypedValueEmpty,
  toStringTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {InvalidPositionError, parseGridPosition} from 'samples/SampleUtil';
import lruMemoize from 'util/lruMemoize';
import {Optional} from 'util/Types';

export const MULTIPLE_CONTENTS_ERROR_MESSAGE = 'Cannot resolve concentration for multiple contents.';
export const NO_CONTENT_ERROR_MESSAGE = 'Container has no contents.';
export const GET_CONTAINER_CONCENTRATION_QUERY = gql`
  # eslint-disable benchlint/id-field-required
  query GetContainerConcentration($containerIds: [StrictString]!) @skipAudit @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        name
        contents {
          concentrationJson
        }
        storableSchema {
          ... on StorableSchemaInterface {
            id
          }
        }
        ...HasArchiveRecordGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const GET_PLATE_WITH_WELL_CONCENTRATIONS_QUERY = gql`
  # plates.cells.container.contents does not query id
  # eslint-disable benchlint/id-field-required
  query GetPlateWithWellConcentrations($plateIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    plates(ids: $plateIds) {
      ... on PlateNode {
        id
        name
        storableSchema {
          ... on PlateSchemaNode {
            id
            height
            width
            plateSchemaType
          }
        }
        cells {
          container {
            ... on ContainerNode {
              id
              name
              contents {
                concentrationJson
              }
            }
            ...InaccessibleInfoGraphQL
          }
        }
        ...HasArchiveRecordGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// To mock our GraphQL call, we need to wrap it in a method.
// This is different from most of our GraphQL tests, where we mock the Apollo cache.
// Here, mocking the cache won't work, because our fetch policy is `'no-cache'`.
export const stubbableFunctions = {
  async doContainerConcentrationQuery(containerId: string): Promise<GetContainerConcentration> {
    return await doQueryWithoutCache<GetContainerConcentration, GetContainerConcentrationVariables>(
      {query: GET_CONTAINER_CONCENTRATION_QUERY},
      {variables: {containerIds: [containerId]}}
    );
  },

  async doPlateConcentrationQuery(plateId: string): Promise<GetPlateWithWellConcentrations> {
    return await doQueryWithoutCache<GetPlateWithWellConcentrations, GetPlateWithWellConcentrationsVariables>(
      {query: GET_PLATE_WITH_WELL_CONCENTRATIONS_QUERY},
      {variables: {plateIds: [plateId]}}
    );
  },
};

export function getConcentrationAmountFromContainerCustomEval<CustomEvalOptions>({
  containerSchemaId,
}: {containerSchemaId?: string | null} = {}): CustomEval<CustomEvalOptions> {
  return (
    concentrationTypedValue: TypedValue,
    columnDefinition: ConcentrationColumnDefinition,
    [containerTypedValue]: [TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    if (!isTypedValueEmpty(concentrationTypedValue)) {
      return {val: concentrationTypedValue, error: null, locked: false};
    }

    const containerId = extractContainerId(containerTypedValue);
    if (containerId == null) {
      return {val: concentrationTypedValue, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      concentrationTypedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_CONCENTRATION_AMOUNT_FROM_CONTAINER,
        containerId,
        ...(containerSchemaId != null && {containerSchemaId}),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );

    // only lock the cell for warnings caused by multiple contents or no content
    const isLocked =
      (error?.message.includes(MULTIPLE_CONTENTS_ERROR_MESSAGE) ||
        error?.message.includes(NO_CONTENT_ERROR_MESSAGE)) ??
      false;

    return {val: resolvedValue, error, locked: isLocked};
  };
}

export function getConcentrationUnitsFromContainerCustomEval<CustomEvalOptions>({
  containerSchemaId,
}: {containerSchemaId?: string | null} = {}): CustomEval<CustomEvalOptions> {
  return (
    concentrationTypedValue: TypedValue,
    columnDefinition: ConcentrationColumnDefinition,
    [containerTypedValue]: [TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    if (!isTypedValueEmpty(concentrationTypedValue)) {
      return {val: concentrationTypedValue, error: null, locked: false};
    }

    const containerId = extractContainerId(containerTypedValue);
    if (containerId == null) {
      return {val: concentrationTypedValue, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      concentrationTypedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_CONCENTRATION_UNITS_FROM_CONTAINER,
        containerId,
        ...(containerSchemaId != null && {containerSchemaId}),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    // only lock the cell for warnings caused by multiple contents or no content
    const isLocked =
      (error?.message.includes(MULTIPLE_CONTENTS_ERROR_MESSAGE) ||
        error?.message.includes(NO_CONTENT_ERROR_MESSAGE)) ??
      false;

    return {val: resolvedValue, error, locked: isLocked};
  };
}

export async function autopopulateContainerConcentration(
  query:
    | InventoryTableConcentrationAmountFromContainerQuery
    | InventoryTableConcentrationUnitsFromContainerQuery,
  measurementType: keyof LegacyMeasurement
): Promise<ServerResolutionResult> {
  const data = await stubbableFunctions.doContainerConcentrationQuery(query.containerId);
  const container = data.containers[0];
  if (isInaccessible(container)) {
    return makeCustomResolutionResultFromTypedValue(null);
  }

  if (query.containerSchemaId != null && query.containerSchemaId !== container.storableSchema.id) {
    return makeCustomResolutionResultFromTypedValue(null);
  }

  // surface concentration as null when there is no content
  if (container.contents.length === 0) {
    return makeCustomResolutionResultFromTypedValue(null, NO_CONTENT_ERROR_MESSAGE, true);
  }

  const concentrations: Array<LegacyMeasurement> = container.contents.map(
    (content) => content.concentrationJson
  );
  const hasMultipleContents: boolean = concentrations.length > 1;
  // surface concentration as null when there is multiple contents or contents with no specified concentration
  if (concentrations.length !== 1) {
    return makeCustomResolutionResultFromTypedValue(
      null,
      hasMultipleContents ? MULTIPLE_CONTENTS_ERROR_MESSAGE : undefined,
      hasMultipleContents
    );
  }

  if (concentrations[0][measurementType] == null) {
    return makeCustomResolutionResultFromTypedValue(null);
  }

  const typedValue = toStringTypedValue(concentrations[0][measurementType].toString());
  return makeCustomResolutionResultFromTypedValue(typedValue);
}

export function getConcentrationAmountFromPlateCustomEval<CustomEvalOptions>({
  plateSchemaId,
}: {plateSchemaId?: string | null} = {}): CustomEval<CustomEvalOptions> {
  return (
    concentrationTypedValue: TypedValue,
    columnDefinition: ConcentrationColumnDefinition,
    [plateTypedValue, positionTypedValue]: [TypedValue | null, TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    const numericResult = getNonnegativeCustomEvalResult(concentrationTypedValue);
    if (!isTypedValueEmpty(numericResult.val) || numericResult.error) {
      return numericResult;
    }

    if (!positionTypedValue || isTypedValueEmpty(positionTypedValue)) {
      return {val: concentrationTypedValue, error: null, locked: false};
    }

    const position = formatTypedValue(positionTypedValue);
    const plateId = extractPlateId(plateTypedValue);
    if (plateId == null) {
      return {val: concentrationTypedValue, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      concentrationTypedValue,
      {
        customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_CONCENTRATION_AMOUNT_FROM_PLATE,
        plateId,
        position,
        ...(plateSchemaId != null && {plateSchemaId}),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    // only lock the cell for warnings caused by multiple contents or no content
    const isLocked =
      (error?.message.includes(MULTIPLE_CONTENTS_ERROR_MESSAGE) ||
        error?.message.includes(NO_CONTENT_ERROR_MESSAGE)) ??
      false;

    return {val: resolvedValue, error, locked: isLocked};
  };
}

export function getConcentrationUnitsFromPlateCustomEval<CustomEvalOptions>({
  plateSchemaId,
}: {plateSchemaId?: string | null} = {}): CustomEval<CustomEvalOptions> {
  return (
    concentrationTypedValue: TypedValue,
    columnDefinition: ConcentrationColumnDefinition,
    [plateTypedValue, positionTypedValue]: [TypedValue | null, TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    if (!isTypedValueEmpty(concentrationTypedValue)) {
      return {val: concentrationTypedValue, error: null, locked: false};
    }

    if (!positionTypedValue || isTypedValueEmpty(positionTypedValue)) {
      return {val: concentrationTypedValue, error: null, locked: false};
    }

    const position = formatTypedValue(positionTypedValue);
    const plateId = extractPlateId(plateTypedValue);
    if (plateId == null) {
      return {val: concentrationTypedValue, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      concentrationTypedValue,
      {
        customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_CONCENTRATION_UNITS_FROM_PLATE,
        plateId,
        position,
        ...(plateSchemaId != null && {plateSchemaId}),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    // only lock the cell for warnings caused by multiple contents or no content
    const isLocked =
      (error?.message.includes(MULTIPLE_CONTENTS_ERROR_MESSAGE) ||
        error?.message.includes(NO_CONTENT_ERROR_MESSAGE)) ??
      false;

    return {val: resolvedValue, error, locked: isLocked};
  };
}

export async function autopopulatePlateConcentration(
  query: InventoryTableConcentrationAmountFromPlateQuery | InventoryTableConcentrationUnitsFromPlateQuery,
  measurementType: keyof LegacyMeasurement
): Promise<ServerResolutionResult> {
  const data = await stubbableFunctions.doPlateConcentrationQuery(query.plateId);

  const plate = data.plates[0];
  if (isInaccessible(plate)) {
    return makeCustomResolutionResultFromTypedValue(null);
  }
  assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');

  if (query.plateSchemaId != null && query.plateSchemaId !== plate.storableSchema.id) {
    return makeCustomResolutionResultFromTypedValue(null);
  }
  if (plate.storableSchema.plateSchemaType !== PlateSchemaType.FIXED_PLATE) {
    return makeCustomResolutionResultFromTypedValue(null);
  }

  let rowIndex: number;
  let columnIndex: number;
  try {
    ({rowIndex, columnIndex} = parseGridPosition({
      position: query.position,
      gridHeight: plate.storableSchema.height,
      gridWidth: plate.storableSchema.width,
    }));
  } catch (e) {
    if (e instanceof InvalidPositionError) {
      return makeCustomResolutionResultFromTypedValue(null);
    } else {
      throw e;
    }
  }

  const well = plate.cells[rowIndex][columnIndex]?.container;
  if (isInaccessible(well) || !well) {
    return makeCustomResolutionResultFromTypedValue(null);
  }

  // surface concentration as null when there is no content
  if (well.contents.length === 0) {
    return makeCustomResolutionResultFromTypedValue(null, NO_CONTENT_ERROR_MESSAGE, true);
  }

  const concentrations: Optional<Array<LegacyMeasurement>> = well.contents.map(
    (content) => content.concentrationJson
  );

  if (concentrations == null) {
    return makeCustomResolutionResultFromTypedValue(null);
  }

  const hasMultipleContents: boolean = concentrations.length > 1;
  // surface concentration as null when there is multiple contents or contents with no specified concentration
  if (concentrations.length !== 1) {
    return makeCustomResolutionResultFromTypedValue(
      null,
      hasMultipleContents ? MULTIPLE_CONTENTS_ERROR_MESSAGE : undefined,
      hasMultipleContents
    );
  }

  if (concentrations[0][measurementType] == null) {
    return makeCustomResolutionResultFromTypedValue(null);
  }

  const typedValue = toStringTypedValue(concentrations[0][measurementType].toString());
  return makeCustomResolutionResultFromTypedValue(typedValue);
}

export const InventoryConcentrationResolver = {
  autopopulateContainerConcentrationAmount: (
    query: InventoryTableConcentrationAmountFromContainerQuery
  ): Promise<ServerResolutionResult> => autopopulateContainerConcentration(query, 'value'),

  autopopulateContainerConcentrationUnits: (
    query: InventoryTableConcentrationAmountFromContainerQuery
  ): Promise<ServerResolutionResult> => autopopulateContainerConcentration(query, 'units'),

  autopopulatePlateConcentrationAmount: (
    query: InventoryTableConcentrationAmountFromPlateQuery
  ): Promise<ServerResolutionResult> => autopopulatePlateConcentration(query, 'value'),

  autopopulatePlateConcentrationUnits: (
    query: InventoryTableConcentrationUnitsFromPlateQuery
  ): Promise<ServerResolutionResult> => autopopulatePlateConcentration(query, 'units'),
};

export const InventoryConcentrationCustomEval = {
  getConcentrationAmountFromContainerCustomEval: lruMemoize(
    getConcentrationAmountFromContainerCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),
  getConcentrationUnitsFromContainerCustomEval: lruMemoize(
    getConcentrationUnitsFromContainerCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),
  getConcentrationAmountFromPlateCustomEval: lruMemoize(
    getConcentrationAmountFromPlateCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),

  getConcentrationUnitsFromPlateCustomEval: lruMemoize(
    getConcentrationUnitsFromPlateCustomEval,
    {max: 100},
    (...args) => stringify(args)
  ),
};
